import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryPipelineStageExecutions, queryPipelineStages } from '@/features/pipelines/data';
import { buildStageHistoryRows } from '@/features/pipelines/hooks/stageHistoryRows';
import {
  buildStageNameFilters,
  buildStagePipelineFilters,
} from '@/features/pipelines/hooks/stagePageFilters';
import { useStageFilterOptions } from '@/features/pipelines/hooks/useStageFilterOptions';
import { useStageHistoryJobs } from '@/features/pipelines/hooks/useStageHistoryJobs';
import { useStageMetricCards } from '@/features/pipelines/hooks/useStageMetricCards';
import { useStagesPageFilterOptionCounts } from '@/features/pipelines/hooks/useStagesPageFilterOptionCounts';
import {
  buildStageCounts,
  buildFallbackStageExecutions,
  buildStageExecutionMetricSummary,
  buildStageStatusCountsByDate,
  resolveStageExecutionTimestamp,
  sortHistoryRows,
} from '@/features/pipelines/hooks/useStagesPageViewModel.helpers';
import { useProjectMetricThresholds } from '@/features/projects';
import {
  formatRangeWindowNoDataText,
  useQualityDateRange,
  type QualityRange,
} from '@/features/quality/hooks';
import { buildBuildMetricOptions } from '@/features/quality/sections/qualityMetricOptions';
import {
  buildMetricsChartModelFromBuckets,
  buildMetricsChartToolbarState,
} from '@/features/shared/components/chartPanels/chartPanelAdapters';
import { useDailyHistoryPanelState } from '@/features/shared/hooks/useDailyHistoryPanelState';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import { createDailyExecutionMetricBucket } from '@/features/shared/utils/chartBucketSeries';
import {
  buildChartDatesForDayRange,
  resolveWindowDayKeys,
} from '@/features/shared/utils/chartDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getChartTitle } from '@/shared/copy/charts';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import {
  queryKeys,
  useIsAuthenticated,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { StageExecutionSortKey } from '@/features/pipelines/components/StageExecutionsTab';
import type {
  StageSortKey,
  StagesTablePanelProps,
  StagesPanelTab,
} from '@/features/pipelines/components/StagesTablePanel';
import type { UseStagesPageViewModelResult } from '@/features/pipelines/hooks/useStagesPageViewModel.types';
import type { UiTone } from '@/theme/uiTokens';
import type { BuildStatus, PipelineStageExecutionListItemDto } from '@flowqa/shared';

export const useStagesPageViewModel = (): UseStagesPageViewModelResult => {
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState<QualityRange>('7d');
  const [activeTab, setActiveTab] = useState<StagesPanelTab>('stages');
  const [stageSearchValue, setStageSearchValue] = useState('');
  const [stageExecutionSearchValue, setStageExecutionSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<BuildStatus[]>([]);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState<string[]>([]);
  const [selectedStageNames, setSelectedStageNames] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:stages');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());
  const {
    page: historyPage,
    pageSize: historyPageSize,
    sortKey: historySortKey,
    sortDirection: historySortDirection,
    isTransitionLoading: isHistoryTransitionLoading,
    setPage: setHistoryPage,
    setPageSize: setHistoryPageSize,
    setSortKey: setHistorySortKey,
    setSortDirection: setHistorySortDirection,
  } = useDailyHistoryPanelState({
    defaultSortKey: 'prefixLabel',
  });
  const [stageColumnVisibilityModel, setStageColumnVisibilityModel] = useState<
    StagesTablePanelProps['stageColumnVisibilityModel']
  >({
    stageName: true,
    status: true,
    latestJobStatusCounts: true,
    executionStatusCounts: true,
    metricsSummary: true,
  });
  const [stageSortKey, setStageSortKey] = useState<StageSortKey>('stageName');
  const [stageSortDirection, setStageSortDirection] = useState<'asc' | 'desc'>('asc');
  const [stageExecutionColumnVisibilityModel, setStageExecutionColumnVisibilityModel] = useState<
    StagesTablePanelProps['stageExecutionColumnVisibilityModel']
  >({
    stageName: true,
    buildKey: true,
    status: true,
  });
  const [stageExecutionSortKey, setStageExecutionSortKey] =
    useState<StageExecutionSortKey>('startedAt');
  const [stageExecutionSortDirection, setStageExecutionSortDirection] = useState<'asc' | 'desc'>(
    'desc',
  );
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useQualityDateRange(range);
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectFilterOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const { tagOptions } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { pipelineOptions, stageOptions } = useStageFilterOptions({
    isAuthenticated,
    effectiveProjectId,
    selectedOrganisationId,
    selectedPipelineIds,
    setSelectedStageNames,
  });
  const { thresholds } = useProjectMetricThresholds(effectiveProjectId);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const showDelta = !isLatestMode && !isAllMode && previousRangeStartIso !== null;
  const forcedLoadState = searchParams.get('loadState');
  const showForcedLoadingState = forcedLoadState === 'loading';

  const baseStageFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildStagePipelineFilters(selectedPipelineIds),
        buildStageNameFilters(selectedStageNames),
        buildInFilter('tags', selectedTags),
      ),
    [selectedPipelineIds, selectedStageNames, selectedStatuses, selectedTags],
  );
  const latestBoundsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/latest-bounds',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
    ),
    enabled: isAuthenticated && isLatestMode,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStageExecutions(
            effectiveProjectId,
            selectedOrganisationId ?? undefined,
            {
              page,
              pageSize,
              sorts: [{ field: 'startedAt', direction: 'desc' }],
              filters: mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                baseStageFilters,
              ),
            },
          ),
      ),
  });
  const latestTimestamp = useMemo(() => {
    return (
      (latestBoundsQuery.data?.items ?? [])
        .map((item) => resolveStageExecutionTimestamp(item))
        .filter((value): value is Date => value instanceof Date)
        .sort((left, right) => right.getTime() - left.getTime())[0] ?? null
    );
  }, [latestBoundsQuery.data?.items]);
  const hasLatestTimestamp = latestTimestamp instanceof Date;
  const effectiveStartDay =
    selectedStartDay ??
    (isLatestMode
      ? hasLatestTimestamp
        ? dayjs(latestTimestamp).format('YYYY-MM-DD')
        : null
      : (rangeStartIso?.slice(0, 10) ?? null));
  const effectiveEndDay =
    selectedEndDay ??
    (isLatestMode
      ? hasLatestTimestamp
        ? dayjs(latestTimestamp).format('YYYY-MM-DD')
        : null
      : (rangeEndIso?.slice(0, 10) ?? null));
  const previousStartDay = selectedStartDay ? null : (previousRangeStartIso?.slice(0, 10) ?? null);
  const previousEndDay = selectedEndDay ? null : (previousRangeEndIso?.slice(0, 10) ?? null);
  const currentChartDates = useMemo(
    () => buildChartDatesForDayRange(effectiveStartDay, effectiveEndDay),
    [effectiveEndDay, effectiveStartDay],
  );
  const previousChartDates = useMemo(
    () => buildChartDatesForDayRange(previousStartDay, previousEndDay),
    [previousEndDay, previousStartDay],
  );

  const currentStagesQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'rows',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      effectiveStartDay,
      effectiveEndDay,
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: mergeFilters(
              buildContainsFilter('stageName', stageSearchValue),
              baseStageFilters,
              buildDateRangeFilters(
                'startedAt',
                effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
                effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
              ),
            ),
          }),
      ),
  });
  const currentStageExecutionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'executions',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      effectiveStartDay,
      effectiveEndDay,
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStageExecutions(
            effectiveProjectId,
            selectedOrganisationId ?? undefined,
            {
              page,
              pageSize,
              sorts: [{ field: 'startedAt', direction: 'desc' }],
              filters: mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                baseStageFilters,
                buildDateRangeFilters(
                  'startedAt',
                  effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
                  effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
                ),
              ),
            },
          ),
      ),
  });
  const {
    jobsByStageId,
    isFetching: areStageJobsFetching,
    isError: hasStageJobsError,
  } = useStageHistoryJobs({
    isAuthenticated,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedProjectId: effectiveProjectId,
    selectedPipelineIds,
    selectedStageNames,
    selectedStatuses,
    selectedTags,
    effectiveStartDay,
    effectiveEndDay,
    isLatestMode,
    hasLatestTimestamp,
  });
  const previousStageExecutionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'executions',
      'previous',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      previousStartDay,
      previousEndDay,
    ),
    enabled: isAuthenticated && showDelta && previousStartDay !== null && previousEndDay !== null,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStageExecutions(
            effectiveProjectId,
            selectedOrganisationId ?? undefined,
            {
              page,
              pageSize,
              sorts: [{ field: 'startedAt', direction: 'desc' }],
              filters: mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                baseStageFilters,
                buildDateRangeFilters(
                  'startedAt',
                  dayjs(previousStartDay).startOf('day').toISOString(),
                  dayjs(previousEndDay).endOf('day').toISOString(),
                ),
              ),
            },
          ),
      ),
  });

  const currentStages = currentStagesQuery.data?.items ?? [];
  const currentStageExecutions = currentStageExecutionsQuery.data?.items ?? [];
  const { countedPipelineOptions, countedStageOptions, countedTagOptions } =
    useStagesPageFilterOptionCounts({
      effectiveEndDay,
      effectiveProjectId,
      effectiveStartDay,
      hasLatestTimestamp,
      isAuthenticated,
      isLatestMode,
      pipelineOptions,
      selectedOrganisationId: selectedOrganisationId ?? null,
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
      selectedTags,
      stageOptions,
      stageSearchValue,
      tagOptions,
    });
  const effectiveStageExecutions = useMemo(
    () =>
      currentStageExecutions.length > 0
        ? currentStageExecutions
        : buildFallbackStageExecutions(currentStages),
    [currentStageExecutions, currentStages],
  );
  const previousStageExecutions = previousStageExecutionsQuery.data?.items ?? [];
  const stageCounts = useMemo(() => buildStageCounts(currentStages), [currentStages]);
  const stageMetricOptions = useMemo(buildBuildMetricOptions, []);
  const currentMetrics = useMemo(
    () => buildStageExecutionMetricSummary(effectiveStageExecutions, currentChartDates),
    [currentChartDates, effectiveStageExecutions],
  );
  const previousMetrics = useMemo(
    () => buildStageExecutionMetricSummary(previousStageExecutions, previousChartDates),
    [previousChartDates, previousStageExecutions],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: null,
        previousMetrics: null,
        thresholds: thresholds.stages,
        frequencyOverride: currentMetrics.frequencyPerDay,
        previousFrequencyOverride: showDelta ? previousMetrics.frequencyPerDay : null,
        avgDurationOverride: currentMetrics.avgDurationSeconds,
        previousAvgDurationOverride: showDelta ? previousMetrics.avgDurationSeconds : null,
        passRateOverride: currentMetrics.passRate,
        previousPassRateOverride: showDelta ? previousMetrics.passRate : null,
        showDelta,
      }),
    [currentMetrics, previousMetrics, showDelta, thresholds.stages],
  );
  const { metricCards } = useStageMetricCards({
    isAuthenticated,
    showDelta,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedProjectId: effectiveProjectId,
    stageSearchValue,
    selectedPipelineIds,
    selectedStageNames,
    selectedStatuses,
    selectedTags,
    previousStartDay,
    previousEndDay,
    baseStageFilters,
    currentStagesLength: currentStages.length,
    currentStageExecutionsLength: effectiveStageExecutions.length,
    previousStageExecutionsLength: previousStageExecutions.length,
    currentStagesLoading: currentStagesQuery.isLoading,
    currentStageCounts: stageCounts,
    stageMetricOptions,
  });
  const toolbarState = useMemo(
    () => ({
      ...buildMetricsChartToolbarState({
        search: stageSearchValue,
        setSearch: setStageSearchValue,
        selectedStatuses,
        setSelectedStatuses,
        selectedTags,
        setSelectedTags,
        noRowsText: formatRangeWindowNoDataText(
          getEmptyStateMessage('entity.noneFound', { entity: 'stageRun' }),
          range,
          Boolean(selectedStartDay ?? selectedEndDay),
        ),
        dateQuickFilter: range === 'all' ? '30d' : range,
        onDateQuickFilterChange: (next) => {
          setRange(next);
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        },
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        latestQueryStartDay: hasLatestTimestamp
          ? dayjs(latestTimestamp).format('YYYY-MM-DD')
          : null,
        latestQueryEndDay: hasLatestTimestamp ? dayjs(latestTimestamp).format('YYYY-MM-DD') : null,
        onFromDateChange: setSelectedStartDay,
        onToDateChange: setSelectedEndDay,
      }),
      selectedEntities: selectedStageNames,
      setSelectedEntities: setSelectedStageNames,
      entityOptions: countedStageOptions,
      entityLabel: 'Stage',
      selectedSuites: selectedPipelineIds,
      setSelectedSuites: setSelectedPipelineIds,
      suiteOptions: countedPipelineOptions,
      suiteLabel: 'Pipeline',
      selectedProjects,
      setSelectedProjects: handleProjectsChange,
      projectOptions: showProjectFilter ? projectFilterOptions : undefined,
    }),
    [
      handleProjectsChange,
      countedPipelineOptions,
      countedStageOptions,
      latestTimestamp,
      hasLatestTimestamp,
      projectFilterOptions,
      range,
      selectedEndDay,
      selectedProjects,
      selectedPipelineIds,
      selectedStartDay,
      selectedStageNames,
      selectedStatuses,
      selectedTags,
      showProjectFilter,
      stageSearchValue,
    ],
  );
  const stageStatusCountsByDate = useMemo(
    () => buildStageStatusCountsByDate(effectiveStageExecutions),
    [effectiveStageExecutions],
  );
  const visibleStageStatuses = useMemo(
    () =>
      selectedStatuses.length > 0
        ? buildStatusOptions
            .map((option) => option.value)
            .filter((status) => selectedStatuses.includes(status))
        : buildStatusOptions.map((option) => option.value),
    [selectedStatuses],
  );
  const stageStatusSeries = useMemo(
    () =>
      visibleStageStatuses.map((status) => ({
        key: status,
        label: buildStatusOptions.find((option) => option.value === status)?.label ?? status,
        colorKey: (status === 'completed'
          ? 'green'
          : status === 'failed'
            ? 'red'
            : status === 'running'
              ? 'blue'
              : 'neutral') as UiTone,
      })),
    [visibleStageStatuses],
  );
  const stageMetricBucketsByDate = useMemo(() => {
    const buckets = new Map<string, ReturnType<typeof createDailyExecutionMetricBucket>>();
    effectiveStageExecutions.forEach((execution) => {
      const timestamp = resolveStageExecutionTimestamp(execution);
      if (!timestamp) return;
      const dayKey = timestamp.toISOString().slice(0, 10);
      const bucket = buckets.get(dayKey) ?? createDailyExecutionMetricBucket();
      bucket.total += 1;
      if (execution.status === 'completed') {
        bucket.completed += 1;
      }
      if (execution.status === 'failed') {
        bucket.failed += 1;
      }
      if (typeof execution.durationMs === 'number') {
        const durationSeconds = execution.durationMs / 1000;
        bucket.durationSumSeconds += durationSeconds;
        bucket.durationCount += 1;
      }
      buckets.set(dayKey, bucket);
    });
    return buckets;
  }, [effectiveStageExecutions]);
  const modelOverride = useMemo(
    () =>
      buildMetricsChartModelFromBuckets({
        statusCountsByDate: stageStatusCountsByDate,
        visibleStatuses: visibleStageStatuses,
        statusSeries: stageStatusSeries,
        metricBucketsByDate: stageMetricBucketsByDate,
        chartDates: currentChartDates,
        metricValueItems,
      }),
    [
      currentChartDates,
      metricValueItems,
      stageMetricBucketsByDate,
      stageStatusCountsByDate,
      stageStatusSeries,
      visibleStageStatuses,
    ],
  );

  const stageExecutionsByStageId = useMemo(() => {
    const map = new Map<string, PipelineStageExecutionListItemDto[]>();
    effectiveStageExecutions.forEach((execution) => {
      const stageId = `${execution.pipelineId ?? 'null'}:${execution.stageName}`;
      const items = map.get(stageId) ?? [];
      items.push(execution);
      map.set(stageId, items);
    });
    return map;
  }, [effectiveStageExecutions]);
  const historyRows = useMemo(() => {
    const builtRows = buildStageHistoryRows(
      currentStages,
      stageExecutionsByStageId,
      jobsByStageId,
      isLatestMode,
    );
    const slicedRows = sortHistoryRows(builtRows, historySortKey, historySortDirection).slice(
      historyPage * historyPageSize,
      historyPage * historyPageSize + historyPageSize,
    );
    return slicedRows;
  }, [
    currentStages,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    isLatestMode,
    jobsByStageId,
    stageExecutionsByStageId,
  ]);
  const historyTotalCount = currentStages.length;
  const historyDateRangeLabel = useMemo(() => {
    if (!effectiveStartDay || !effectiveEndDay) return null;
    const startLabel = dayjs(effectiveStartDay).format('MMM D, YYYY');
    const endLabel = dayjs(effectiveEndDay).format('MMM D, YYYY');
    return `${startLabel} - ${endLabel}`;
  }, [effectiveEndDay, effectiveStartDay]);
  const historyDayKeys = useMemo(
    () =>
      resolveWindowDayKeys({
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        fallbackStartDay: effectiveStartDay,
        fallbackEndDay: effectiveEndDay,
        descending: true,
      }),
    [effectiveEndDay, effectiveStartDay, selectedEndDay, selectedStartDay],
  );

  useEffect(() => {
    setHistoryPage(0);
    setExpandedRowIds(new Set());
  }, [
    effectiveEndDay,
    effectiveStartDay,
    selectedOrganisationId,
    selectedPipelineIds,
    effectiveProjectId,
    selectedStatuses,
    selectedStageNames,
    selectedTags,
    stageSearchValue,
  ]);

  return {
    metricSectionProps: {
      title: getChartTitle('statusTimeline'),
      searchAriaLabel: 'Search stages',
      noRowsBaseText: getEmptyStateMessage('entity.noneFound', { entity: 'stageRun' }),
      scope: 'stage',
      projectId: effectiveProjectId,
      availableTags: countedTagOptions,
      statusFilterLabel: getChartTitle('status'),
      metricCards,
      statusOptions: buildStatusOptions,
      toolbarState,
      modelOverride,
      loading: showForcedLoadingState,
      suppressAnimation: false,
      includeLatestQuickFilter: true,
    },
    historyRows,
    historyDayKeys,
    historyLoading:
      showForcedLoadingState ||
      isHistoryTransitionLoading ||
      currentStagesQuery.isFetching ||
      currentStageExecutionsQuery.isFetching ||
      areStageJobsFetching ||
      latestBoundsQuery.isFetching,
    historyHasError:
      currentStagesQuery.isError ||
      currentStageExecutionsQuery.isError ||
      latestBoundsQuery.isError ||
      hasStageJobsError,
    historyNoRowsText: formatRangeWindowNoDataText(
      getEmptyStateMessage('entity.noneFound', { entity: 'stageRun' }),
      range,
      Boolean(selectedStartDay ?? selectedEndDay),
    ),
    historyDateRangeLabel,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    historyTotalCount,
    expandedRowIds,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    setExpandedRowIds,
    tablePanelProps: {
      selectedProjectId: effectiveProjectId,
      selectedOrganisationId: selectedOrganisationId ?? null,
      tagOptions,
      activeTab,
      onActiveTabChange: setActiveTab,
      dateQuickFilter: range === 'all' ? '30d' : range,
      onDateQuickFilterChange: (next) => {
        setRange(next);
        setSelectedStartDay(null);
        setSelectedEndDay(null);
      },
      fromDate: selectedStartDay ? dayjs(selectedStartDay) : null,
      toDate: selectedEndDay ? dayjs(selectedEndDay) : null,
      latestQueryStartDay: hasLatestTimestamp ? dayjs(latestTimestamp).format('YYYY-MM-DD') : null,
      latestQueryEndDay: hasLatestTimestamp ? dayjs(latestTimestamp).format('YYYY-MM-DD') : null,
      onFromDateChange: (next) => {
        setSelectedStartDay(next ? next.format('YYYY-MM-DD') : null);
      },
      onToDateChange: (next) => {
        setSelectedEndDay(next ? next.format('YYYY-MM-DD') : null);
      },
      stageSearchValue,
      onStageSearchChange: setStageSearchValue,
      stageColumnVisibilityModel,
      onStageColumnVisibilityModelChange: setStageColumnVisibilityModel,
      stageSortKey,
      stageSortDirection,
      onStageSortChange: (nextSortKey, nextSortDirection) => {
        setStageSortKey(nextSortKey);
        setStageSortDirection(nextSortDirection);
      },
      stageFilterStatuses: selectedStatuses,
      stageFilterPipelineIds: selectedPipelineIds,
      stageFilterStageNames: selectedStageNames,
      onStageFilterStatusesChange: setSelectedStatuses,
      stageFilterTags: selectedTags,
      onStageFilterTagsChange: setSelectedTags,
      stageSummaryCount: currentStages.length,
      stageExecutionSearchValue,
      onStageExecutionSearchChange: setStageExecutionSearchValue,
      stageExecutionColumnVisibilityModel,
      onStageExecutionColumnVisibilityModelChange: setStageExecutionColumnVisibilityModel,
      stageExecutionSortKey,
      stageExecutionSortDirection,
      onStageExecutionSortChange: (nextSortKey, nextSortDirection) => {
        setStageExecutionSortKey(nextSortKey);
        setStageExecutionSortDirection(nextSortDirection);
      },
      stageExecutionFilterStatuses: selectedStatuses,
      stageExecutionFilterPipelineIds: selectedPipelineIds,
      stageExecutionFilterStageNames: selectedStageNames,
      onStageExecutionFilterStatusesChange: setSelectedStatuses,
      stageExecutionFilterTags: selectedTags,
      onStageExecutionFilterTagsChange: setSelectedTags,
      stageExecutionSummaryCount: effectiveStageExecutions.length,
    },
  };
};
